import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LocationSelector } from '@/components/LocationSelector';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import {
  Menu, LogOut, Calculator, MessageSquare, LayoutDashboard, PlusCircle, Heart, ChevronDown, Sparkles, Crown,
  Phone, Mail, TrendingUp, BookOpen, Coins, Receipt, BarChart3, Trophy, Map, Building2, Home, Truck, Wrench, Info, Scale, LifeBuoy, Store
} from 'lucide-react';
import { getUnreadCount } from '@/lib/api';
import { useChat } from '@/contexts/ChatContext';

const PRIMARY = [
  { to: '/properties?listing_type=buy', label: 'Buy', id: 'buy', match: (s) => s.includes('listing_type=buy') },
  { to: '/properties?listing_type=rent', label: 'Rent', id: 'rent', match: (s) => s.includes('listing_type=rent') },
  { to: '/properties?property_type=commercial', label: 'Commercial', id: 'commercial', match: (s) => s.includes('property_type=commercial') },
  { to: '/properties?is_project=true', label: 'Projects', id: 'projects', match: (s) => s.includes('is_project=true') },
  { to: '/map-search', label: 'Map Search', id: 'map-search', icon: Map, match: (s, p) => p.startsWith('/map-search') },
];

const RESOURCES = [
  { group: 'Tools', items: [
    { href: '/emi-calculator', icon: Calculator, title: 'EMI Calculator' },
    { href: '/affordability-calculator', icon: Coins, title: 'Affordability' },
    { href: '/stamp-duty-calculator', icon: Receipt, title: 'Stamp Duty' },
    { href: '/rental-yield-calculator', icon: TrendingUp, title: 'Rental Yield' },
    { href: '/quiz', icon: Sparkles, title: 'Property Match Quiz' },
    { href: '/knowledge-quiz', icon: Trophy, title: 'Knowledge Quiz' },
  ]},
  { group: 'Insights', items: [
    { href: '/market-trends', icon: BarChart3, title: 'Market Insights' },
    { href: '/glossary', icon: BookOpen, title: 'Glossary' },
    { href: '/compare', icon: Scale, title: 'Compare Properties' },
    { href: '/help-center', icon: LifeBuoy, title: 'Help Center / FAQs' },
    { href: '/about', icon: Info, title: 'About Us' },
  ]},
  { group: 'Services', items: [
    { href: '/professionals', icon: Store, title: 'Professional Services' },
    { href: '/become-a-partner', icon: Store, title: 'Become a Partner' },
    { href: '/packers-movers', icon: Truck, title: 'Packers & Movers' },
    { href: '/become-a-pro', icon: Trophy, title: 'Become a Pro' },
    { href: '/premium', icon: Crown, title: 'Premium Plans' },
  ]},
];

const Navbar = () => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const { user, logout } = useAuth();
  const { open: chatOpen, toggle: toggleChat } = useChat();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) getUnreadCount().then((d) => setUnreadCount(d.count)).catch(() => {});
  }, [user, location]);

  const handleLogout = async () => { await logout(); navigate('/'); };
  const isActive = (item) => item.match(location.search, location.pathname) && (location.pathname.startsWith('/properties') || location.pathname.startsWith('/map-search'));

  const linkCls = (active) => `relative px-2 xl:px-2.5 py-2 text-sm font-semibold rounded-md whitespace-nowrap transition-colors ${active ? 'text-[#0F172A]' : 'text-slate-600 hover:text-[#0F172A] hover:bg-slate-100'} after:absolute after:left-2.5 after:right-2.5 after:-bottom-[17px] after:h-[2px] after:rounded-full after:bg-[#D4AF37] after:transition-opacity ${active ? 'after:opacity-100' : 'after:opacity-0'}`;

  return (
    <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 backdrop-blur-md border-b border-slate-200" data-testid="navbar-container">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-10">
        <div className="flex items-center h-[72px] lg:h-20 gap-2">
          <Link to="/" aria-label="Bharat Estate Home" className="flex items-center flex-shrink-0 cursor-pointer" data-testid="navbar-logo-link">
            <img src="/logo-wide.png" alt="Bharat Estate" className="h-12 lg:h-14 w-auto" />
          </Link>
          <div className="hidden md:block"><LocationSelector compact /></div>

          <nav className="hidden lg:flex items-center gap-0.5 ml-2" aria-label="Primary">
            {PRIMARY.map((item) => (
              <Link key={item.id} to={item.to} className={linkCls(isActive(item))} data-testid={`navbar-link-${item.id}`}>
                <span className="inline-flex items-center gap-1.5">{item.icon && <item.icon className="h-4 w-4" />}{item.label}</span>
              </Link>
            ))}
            <Link to="/services" className={linkCls(location.pathname.startsWith('/services'))} data-testid="navbar-link-book-service">
              <span className="inline-flex items-center gap-1.5"><Wrench className="h-4 w-4" /> Book a Service</span>
            </Link>
            <div className="relative" onMouseEnter={() => setResourcesOpen(true)} onMouseLeave={() => setResourcesOpen(false)}>
              <button className={`${linkCls(false)} inline-flex items-center gap-1`} data-testid="navbar-link-resources" onClick={() => setResourcesOpen((v) => !v)}>
                Resources <ChevronDown className={`h-4 w-4 transition-transform ${resourcesOpen ? 'rotate-180' : ''}`} />
              </button>
              {resourcesOpen && (
                <div className="absolute left-0 top-full pt-3" data-testid="navbar-resources-menu">
                  <div className="w-[640px] bg-white border border-slate-200 rounded-xl shadow-2xl p-6 grid grid-cols-3 gap-6 animate-in fade-in slide-in-from-top-1 duration-150">
                    {RESOURCES.map((g) => (
                      <div key={g.group}>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-3">{g.group}</p>
                        <ul className="space-y-1">
                          {g.items.map((i) => (
                            <li key={i.href}>
                              <Link to={i.href} onClick={() => setResourcesOpen(false)} className="flex items-center gap-2.5 px-2 py-1.5 -mx-2 rounded-md text-sm text-slate-700 hover:bg-slate-50 hover:text-[#0F172A]">
                                <i.icon className="h-4 w-4 text-slate-400" /> {i.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      </div>
                    ))}
                    <div className="col-span-3 border-t border-slate-100 pt-4 flex items-center gap-6 text-xs text-slate-500">
                      <a href="tel:+919465024516" className="inline-flex items-center gap-1.5 hover:text-[#0F172A]"><Phone className="h-3.5 w-3.5" /> +91 94650 24516</a>
                      <a href="mailto:contact@bharat.estate" className="inline-flex items-center gap-1.5 hover:text-[#0F172A]"><Mail className="h-3.5 w-3.5" /> contact@bharat.estate</a>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </nav>

          <div className="ml-auto hidden md:flex items-center gap-1">
            <Link to="/add-property" className="hidden lg:inline-flex" data-testid="navbar-link-post-property">
              <Button variant="outline" className="h-10 rounded-full border-slate-300 text-[#0F172A] font-semibold px-3.5 hover:border-[#0F172A] hover:bg-white">
                <PlusCircle className="h-4 w-4 mr-1.5" /> Post Property
              </Button>
            </Link>
            <Link to={user ? '/dashboard?tab=saved' : '/login'} className="h-10 w-10 rounded-full inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-[#0F172A]" title="Saved" data-testid="navbar-saved-button">
              <Heart className="h-5 w-5" />
            </Link>
            {user && (
              <Link to="/messages" className="relative h-10 w-10 rounded-full inline-flex items-center justify-center text-slate-600 hover:bg-slate-100 hover:text-[#0F172A]" data-testid="messages-link">
                <MessageSquare className="h-5 w-5" />
                {unreadCount > 0 && <span className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-[#D4AF37] text-[#0F172A] text-[10px] font-bold rounded-full flex items-center justify-center">{unreadCount}</span>}
              </Link>
            )}
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="flex items-center gap-2 pl-1 pr-2 h-10 rounded-full hover:bg-slate-100" data-testid="user-menu-trigger">
                    {user.picture ? <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full object-cover" /> : (
                      <div className="h-8 w-8 rounded-full bg-[#0F172A] text-white text-sm font-semibold flex items-center justify-center">{user.name?.[0]?.toUpperCase()}</div>
                    )}
                    <ChevronDown className="h-4 w-4 text-slate-500" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-60">
                  <div className="px-2 py-1.5"><p className="text-sm font-semibold">{user.name}</p><p className="text-xs text-muted-foreground truncate">{user.email}</p></div>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild><Link to="/dashboard" className="flex items-center gap-2" data-testid="dashboard-link"><LayoutDashboard className="h-4 w-4" /> Dashboard</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/add-property" className="flex items-center gap-2" data-testid="add-property-link"><PlusCircle className="h-4 w-4" /> Post Property</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/dashboard?tab=saved" className="flex items-center gap-2" data-testid="saved-link"><Heart className="h-4 w-4" /> Saved Properties</Link></DropdownMenuItem>
                  <DropdownMenuItem asChild><Link to="/premium" className="flex items-center gap-2 text-[#B5952F]"><Crown className="h-4 w-4" /> Premium · First month free</Link></DropdownMenuItem>
                  {user.is_admin && <DropdownMenuItem asChild><Link to="/admin" className="flex items-center gap-2" data-testid="nav-admin-link"><LayoutDashboard className="h-4 w-4" /> Admin Dashboard</Link></DropdownMenuItem>}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-600" data-testid="logout-btn"><LogOut className="h-4 w-4 mr-2" /> Logout</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <Link to="/login" data-testid="navbar-login-button">
                <Button className="h-10 rounded-full bg-[#0F172A] hover:bg-[#1E293B] text-white font-semibold px-5">Sign In</Button>
              </Link>
            )}
            <button onClick={toggleChat} className={`h-10 rounded-full px-3.5 inline-flex items-center gap-1.5 text-sm font-bold transition-colors ${chatOpen ? 'bg-[#0F172A] text-[#D4AF37]' : 'bg-[#D4AF37] text-[#0F172A] hover:bg-[#e0bf48]'}`} data-testid="navbar-help-button" aria-label="Help — chat with Bharat AI">
              <Sparkles className="h-4 w-4" /> Help
            </button>
          </div>

          <button onClick={toggleChat} className="md:hidden ml-auto h-9 rounded-full px-3 inline-flex items-center gap-1 text-xs font-bold bg-[#D4AF37] text-[#0F172A]" data-testid="navbar-help-button-mobile" aria-label="Help">
            <Sparkles className="h-3.5 w-3.5" /> Help
          </button>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild className="lg:hidden md:ml-0">
              <Button variant="ghost" size="icon" data-testid="mobile-menu-btn"><Menu className="h-6 w-6 text-[#0F172A]" /></Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-80 overflow-y-auto">
              <SheetTitle className="sr-only">Navigation Menu</SheetTitle>
              <SheetDescription className="sr-only">Browse properties, tools, and your account</SheetDescription>
              <div className="flex flex-col gap-1 mt-6">
                <div className="py-2"><LocationSelector /></div>
                {[{ to: '/', label: 'Home', icon: Home }, ...PRIMARY.map((p) => ({ to: p.to, label: p.label, icon: p.icon || Building2 }))].map((l) => (
                  <Link key={l.to} to={l.to} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2.5 text-base font-semibold text-[#0F172A]" data-testid={`mobile-link-${l.label.toLowerCase().replace(' ', '-')}`}>
                    <l.icon className="h-5 w-5 text-slate-500" /> {l.label}
                  </Link>
                ))}
                <Link to="/add-property" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2.5 text-base font-semibold text-[#0F172A]"><PlusCircle className="h-5 w-5 text-slate-500" /> Post Property</Link>
                <Link to="/services" onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2.5 text-base font-semibold text-[#0F172A]" data-testid="mobile-link-book-service"><Wrench className="h-5 w-5 text-slate-500" /> Book a Service</Link>
                {RESOURCES.map((g) => (
                  <div key={g.group} className="border-t pt-3 mt-2">
                    <p className="text-xs uppercase tracking-wider text-slate-400 mb-2">{g.group}</p>
                    {g.items.map((i) => (
                      <Link key={i.href} to={i.href} onClick={() => setMobileOpen(false)} className="flex items-center gap-3 py-2 text-sm text-slate-700"><i.icon className="h-4 w-4 text-slate-400" /> {i.title}</Link>
                    ))}
                  </div>
                ))}
                <div className="border-t pt-5 mt-2">
                  {user ? (
                    <>
                      <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 font-medium"><LayoutDashboard className="h-5 w-5" /> Dashboard</Link>
                      <Link to="/messages" onClick={() => setMobileOpen(false)} className="flex items-center gap-2 py-2 font-medium"><MessageSquare className="h-5 w-5" /> Messages{unreadCount > 0 && <span className="ml-2 px-2 py-0.5 bg-[#D4AF37] text-[#0F172A] text-xs rounded-full">{unreadCount}</span>}</Link>
                      <Button onClick={handleLogout} variant="outline" className="w-full mt-4" data-testid="mobile-logout-btn"><LogOut className="h-4 w-4 mr-2" /> Logout</Button>
                    </>
                  ) : (
                    <div className="space-y-3">
                      <Link to="/login" onClick={() => setMobileOpen(false)}><Button variant="outline" className="w-full rounded-full">Sign In</Button></Link>
                      <Link to="/register" onClick={() => setMobileOpen(false)}><Button className="w-full rounded-full bg-[#0F172A] text-white hover:bg-[#1E293B]">Create Account</Button></Link>
                    </div>
                  )}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
